<template>
  <div class="rv-upcoming" data-test="reviews-upcoming">
    <div class="rv-section-head">
      <div>
        <h2 class="rv-h2">Upcoming appointments</h2>
        <div class="rv-h2-sub">
          See who will be asked for a review after their visit, and skip a visit before it happens.
          <span v-if="sendTimeLabel"> Requests go out {{ sendTimeLabel }}.</span>
        </div>
      </div>
    </div>

    <!-- Day chips: today … today + 7, in the SITE timezone. -->
    <!-- Toggle buttons (aria-pressed), not role="tab": there is no tabpanel / arrow-key model. -->
    <div class="rv-day-chips" role="group" aria-label="Choose a day" data-test="upcoming-days">
      <button
        v-for="d in days"
        :key="d.ymd"
        type="button"
        class="rv-day-chip"
        :aria-pressed="selectedDate === d.ymd ? 'true' : 'false'"
        :class="{ active: selectedDate === d.ymd }"
        :data-test="'upcoming-day-' + d.ymd"
        @click="selectDay(d.ymd)"
      >
        <span class="rv-day-label">{{ d.label }}</span>
        <span class="rv-day-sub">{{ d.sub }}</span>
      </button>
    </div>

    <div v-if="upcoming && upcoming.automationEnabled === false" class="rv-off-banner" data-test="upcoming-automation-off">
      <i class="fa fa-pause-circle" aria-hidden="true"></i>
      <span><b>Automatic review requests are off — nothing will be sent.</b> Turn them on under Settings.</span>
    </div>

    <div class="rv-card rv-table-card">
      <div class="rv-table-head">
        <span>Time</span>
        <span>Patient</span>
        <span>Provider</span>
        <span>Review</span>
        <span class="rv-right">Action</span>
      </div>

      <div v-if="loading && !rows.length" class="rv-table-empty">
        <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
      </div>

      <div v-else-if="loadFailed" class="rv-table-empty">
        <i class="fa fa-exclamation-triangle"></i>
        <span>Couldn't load appointments for this day.</span>
        <button type="button" class="rv-row-btn" @click="load">Try again</button>
      </div>

      <div v-else-if="!rows.length" class="rv-table-empty">
        <i class="fa fa-calendar-o"></i>
        <span>No appointments scheduled {{ selectedDayLabel }}.</span>
      </div>

      <div
        v-else
        v-for="row in rows"
        :key="row.aptNum"
        class="rv-row"
        :class="{ 'is-off': row.state === 'skipped' || row.state === 'blocked' }"
        data-test="upcoming-row"
      >
        <div class="rv-cell">
          <div class="rv-appt">{{ formatTime(row.aptDateTime) }}</div>
          <div class="rv-appt-sub" v-if="row.opName">{{ row.opName }}</div>
        </div>

        <div class="rv-cell rv-patient">
          <div class="rv-avatar">{{ initials(row.patientName) }}</div>
          <div class="rv-patient-meta">
            <div class="rv-name">{{ row.patientName || 'Unknown patient' }}</div>
            <div class="rv-pid">#{{ row.patNum }}</div>
          </div>
        </div>

        <div class="rv-cell rv-provider">{{ row.provider || '—' }}</div>

        <div class="rv-cell">
          <span class="rv-status-dot" :class="'is-' + chip(row).tone" data-test="upcoming-state">
            <span class="rv-dot"></span>{{ chip(row).label }}
          </span>
          <div v-if="chip(row).detail" class="rv-status-when">{{ chip(row).detail }}</div>
        </div>

        <div class="rv-cell rv-right rv-actions">
          <button
            v-if="row.canSkip"
            type="button"
            class="rv-row-btn"
            data-test="upcoming-skip"
            :disabled="isBusy(row)"
            :aria-label="'Skip review request for ' + (row.patientName || 'this patient')"
            title="Don't ask this patient for a review after this visit"
            @click="skip(row)"
          >{{ isBusy(row) ? 'Skipping…' : 'Skip' }}</button>
          <button
            v-else-if="row.canUnskip"
            type="button"
            class="rv-row-btn rv-row-btn-undo"
            data-test="upcoming-undo"
            :disabled="isBusy(row)"
            :aria-label="'Undo skipped review request for ' + (row.patientName || 'this patient')"
            @click="unskip(row)"
          >{{ isBusy(row) ? 'Undoing…' : 'Undo' }}</button>
          <span v-else class="rv-no-action">—</span>
        </div>
      </div>
    </div>

    <p class="rv-footnote">
      Predictions use today's rules and the treatment planned for each visit, so the final result can
      differ once the visit is completed in Open Dental.
    </p>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex';
import moment from 'moment-timezone';
import mixin from '../../../mixin.js';
import { buildUpcomingDays, upcomingStateChip, skipReasonLabel, errorForLog } from '../doNotSend';

// How often an open tab re-checks the site-local date, so "Today" rolls over at midnight.
const CLOCK_TICK_MS = 5 * 60 * 1000;

export default {
  name: 'ReviewsUpcoming',
  mixins: [mixin],

  data() {
    return {
      selectedDate: '',
      busyAptNums: [],
      loadFailed: false,
      // The clock the day chips are built from. A computed over moment.tz() alone would cache
      // forever (it only depends on the timezone), so "Today" would go stale after midnight.
      now: Date.now(),
    };
  },

  created() {
    // Not reactive on purpose: only the latest load() may set or clear loadFailed.
    this.loadSeq = 0;
    this.clockTimer = null;
    this.selectedDate = this.todayYmd;
    this.load();
  },

  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.clockTimer = setInterval(this.onClockTick, CLOCK_TICK_MS);
  },

  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.clockTimer) clearInterval(this.clockTimer);
    this.clockTimer = null;
  },

  computed: {
    ...mapState('reviews', {
      upcoming: (s) => s.upcoming,
      loading: (s) => s.loading.upcoming,
    }),

    timezone() {
      return (this.allSettings
        && this.allSettings.GENERAL_TIMEZONE
        && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || 'America/New_York';
    },

    // Depends on `now` (refreshed by load(), visibilitychange and a 5-minute tick) — see data().
    todayYmd() {
      return moment.tz(this.now, this.timezone).format('YYYY-MM-DD');
    },

    days() {
      return buildUpcomingDays(this.todayYmd, 8);
    },

    // Only show rows that belong to the chip that is selected — the store keeps the last
    // response, which may be for another day while a new one loads.
    rows() {
      const u = this.upcoming;
      if (!u || u.date !== this.selectedDate || !Array.isArray(u.rows)) return [];
      return u.rows;
    },

    sendTimeLabel() {
      return (this.upcoming && this.upcoming.sendTimeLabel) || '';
    },

    selectedDayLabel() {
      const d = this.days.find((x) => x.ymd === this.selectedDate);
      if (!d) return 'on this day';
      if (d.label === 'Today') return 'today';
      if (d.label === 'Tomorrow') return 'tomorrow';
      return `on ${d.label} ${d.sub}`;
    },
  },

  methods: {
    ...mapActions('reviews', ['getUpcomingAppointments', 'skipReviewForAppointment', 'unskipReviewForAppointment']),

    async load() {
      // Every load re-reads the clock; past midnight that moves the office back onto the new Today.
      this.refreshNow();
      if (!this.siteID || !this.selectedDate) return;
      const seq = ++this.loadSeq;
      this.loadFailed = false;
      const date = this.selectedDate;
      try {
        await this.getUpcomingAppointments({ siteID: this.siteID, date });
        if (seq === this.loadSeq) this.loadFailed = false;
      } catch (err) {
        console.error('[ReviewsUpcoming] load failed:', errorForLog(err));
        // A slower, older call must never flag the day the office is looking at now.
        if (seq !== this.loadSeq || date !== this.selectedDate) return;
        this.loadFailed = true;
        this.$toast && this.$toast.error((err && err.code && err.message) || 'Could not load upcoming appointments.');
      }
    },

    // Clicking the chip that is already selected reloads it (a stale "Today" is refreshed, too).
    selectDay(ymd) {
      this.selectedDate = ymd;
      this.load();
    },

    /**
     * Re-read the clock. When the site-local day has rolled over since the last read, the old
     * "Today" is gone from the chips, so the selection resets to the new today.
     * → true when the day rolled over.
     */
    refreshNow() {
      const before = this.todayYmd;
      this.now = Date.now();
      const after = this.todayYmd;
      if (after === before) return false;
      this.selectedDate = after;
      return true;
    },

    onClockTick() {
      if (this.refreshNow()) this.load();
    },

    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.onClockTick();
    },

    chip(row) {
      return upcomingStateChip(row, { formatTime: (v) => this.formatSendAt(v) });
    },

    isBusy(row) {
      return this.busyAptNums.includes(row.aptNum);
    },

    async skip(row) {
      if (this.isBusy(row)) return;
      this.busyAptNums.push(row.aptNum);
      try {
        const data = await this.skipReviewForAppointment({
          siteID: this.siteID,
          aptNum: row.aptNum,
          employeeID: this.user && this.user.EmployeeID,
        });
        let msg = 'Review request skipped for this visit.';
        if (data && data.alreadySkipped) {
          msg = data.ruleReason
            ? `Already skipped: ${skipReasonLabel(data.ruleReason)}.`
            : 'This visit was already skipped.';
        }
        this.$toast && this.$toast.success(msg);
        // Reload BEFORE the row stops being busy: clearing first re-enabled the button while the
        // old row was still on screen, so a double-click fired Skip twice.
        await this.load();
      } catch (err) {
        console.error('[ReviewsUpcoming] skip failed:', errorForLog(err));
        // OUT_OF_WINDOW (400), NOT_FOUND (404), ALREADY_SENT (409): the server's sentence is the answer.
        this.$toast && this.$toast.error((err && err.code && err.message) || 'Could not skip this visit.');
        await this.load();
      } finally {
        this.busyAptNums = this.busyAptNums.filter((n) => n !== row.aptNum);
      }
    },

    async unskip(row) {
      if (this.isBusy(row)) return;
      this.busyAptNums.push(row.aptNum);
      try {
        await this.unskipReviewForAppointment({
          siteID: this.siteID,
          aptNum: row.aptNum,
          employeeID: this.user && this.user.EmployeeID,
        });
        this.$toast && this.$toast.success('Skip undone — this visit will be asked as usual.');
        await this.load(); // before clearing busy — see skip()
      } catch (err) {
        console.error('[ReviewsUpcoming] unskip failed:', errorForLog(err));
        // NOT_SKIPPED (404), VISIT_COMPLETED (409).
        this.$toast && this.$toast.error((err && err.code && err.message) || 'Could not undo the skip.');
        await this.load();
      } finally {
        this.busyAptNums = this.busyAptNums.filter((n) => n !== row.aptNum);
      }
    },

    // aptDateTime / sendAt are SITE-LOCAL wall clock — parsed IN the zone, never converted.
    parseLocal(value) {
      if (!value) return null;
      const m = moment.tz(String(value).replace('T', ' ').slice(0, 19), 'YYYY-MM-DD HH:mm:ss', this.timezone);
      return m.isValid() ? m : null;
    },

    formatTime(value) {
      const m = this.parseLocal(value);
      return m ? m.format('h:mm A') : '—';
    },

    // "7:00 PM" when the request goes out on the visit's own day, "Sep 16, 7:00 PM" otherwise.
    formatSendAt(value) {
      const m = this.parseLocal(value);
      if (!m) return '';
      return m.format('YYYY-MM-DD') === this.selectedDate ? m.format('h:mm A') : m.format('MMM D, h:mm A');
    },

    initials(name) {
      if (!name) return '?';
      return name.split(/\s+/).filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join('');
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #0f766e;
$border: #e2e8f0;
$neutral-500: #64748b;
$neutral-400: #94a3b8;
$neutral-900: #0f172a;

.rv-upcoming { display: flex; flex-direction: column; gap: 16px; }

.rv-section-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.rv-h2 { font-size: 16px; font-weight: 800; margin: 0; color: $neutral-900; }
.rv-h2-sub { font-size: 12.5px; color: $neutral-500; margin-top: 2px; max-width: 640px; }

/* Day chips wrap onto a second line on narrow screens instead of scrolling the page sideways. */
.rv-day-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.rv-day-chip {
  display: flex; flex-direction: column; align-items: flex-start; gap: 1px;
  min-width: 84px; padding: 7px 12px;
  border: 1px solid $border; border-radius: 10px; background: #fff; cursor: pointer; font-family: inherit;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover { border-color: #9fd7cd; }
  &.active { border-color: $primary; background: #edfbf6; }
}
.rv-day-label { font-size: 13px; font-weight: 700; color: $neutral-900; }
.rv-day-sub { font-size: 11.5px; color: $neutral-500; }
.rv-day-chip.active .rv-day-label { color: $primary; }

.rv-off-banner {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 14px; border-radius: 10px;
  background: #fffbeb; border: 1px solid #fde68a; color: #78350f;
  font-size: 13px; line-height: 1.5;
  .fa { color: #b45309; font-size: 15px; margin-top: 2px; }
  b { font-weight: 700; }
}

.rv-card { background: #fff; border: 1px solid $border; border-radius: 14px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); }
.rv-table-card { overflow: hidden; }

.rv-table-head, .rv-row {
  display: grid;
  grid-template-columns: 0.8fr 1.6fr 1.1fr 1.8fr 0.8fr;
  gap: 14px;
  padding: 12px 18px;
  align-items: center;
}
.rv-table-head {
  border-bottom: 1px solid $border;
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: $neutral-500;
}
.rv-row {
  border-bottom: 1px solid #f1f5f9;
  &:hover { background: #fafbfb; }
  &.is-off .rv-patient, &.is-off .rv-provider { opacity: 0.6; }
}
.rv-right { text-align: right; justify-content: flex-end; display: flex; }
.rv-cell { min-width: 0; }
.rv-actions { align-items: center; gap: 10px; }

.rv-patient { display: flex; align-items: center; gap: 11px; }
.rv-avatar {
  width: 32px; height: 32px; border-radius: 999px; flex-shrink: 0;
  background: #e2e8f0; color: #475569; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.rv-patient-meta { min-width: 0; }
.rv-name { font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: $neutral-900; }
.rv-pid { font-size: 12px; color: $neutral-500; font-family: monospace; }
.rv-appt { font-weight: 600; font-size: 13px; white-space: nowrap; }
.rv-appt-sub { font-size: 12px; color: $neutral-500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rv-provider { font-size: 13px; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.rv-status-dot {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12.5px; font-weight: 600; color: #334155;
  .rv-dot { width: 8px; height: 8px; border-radius: 999px; background: #94a3b8; flex-shrink: 0; }
  &.is-blue .rv-dot { background: #3b82f6; }
  &.is-teal .rv-dot { background: #0d9488; }
  &.is-amber .rv-dot { background: #f59e0b; }
  &.is-gray .rv-dot { background: #cbd5e1; }
  &.is-neutral .rv-dot { background: #94a3b8; }
}
.rv-status-when { font-size: 11.5px; color: $neutral-400; margin-top: 2px; }

.rv-row-btn {
  border: 1px solid $border; background: #fff; color: $neutral-500; font-family: inherit;
  font-size: 12px; font-weight: 600; cursor: pointer;
  padding: 5px 12px; border-radius: 7px; white-space: nowrap;
  &:hover:not(:disabled) { border-color: #fca5a5; color: #b91c1c; background: #fef2f2; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}
.rv-row-btn-undo:hover:not(:disabled) { border-color: #9fd7cd; color: $primary; background: #edfbf6; }
.rv-no-action { color: $neutral-400; }

.rv-table-empty {
  display: flex; align-items: center; justify-content: center; gap: 10px; flex-wrap: wrap;
  padding: 44px 18px; color: $neutral-500; font-size: 13.5px;
  .fa { font-size: 18px; color: $neutral-400; }
}

.rv-footnote { font-size: 12px; color: $neutral-400; margin: 0; }

@media (max-width: 991px) {
  .rv-table-head { display: none; }
  .rv-row { grid-template-columns: 1fr 1fr; row-gap: 8px; }
  .rv-right { grid-column: 2; }
}
</style>
